import type { TFunction } from "i18next";
import {
  AuditLogActorKind,
  type AuditLogActorDto,
  type AuditLogEntryDto,
  type AuditLogFiltersDto,
  type AuditLogItemDto,
  type AuditLogPersonDto,
} from "../../../../../autogen/bff-api";
import type { ListFormat } from "../../../../../common/download/ListDownloadItems";
import { isTooLargeToDownload, type ExportColumn } from "../../../../../common/download/listExport";
import { urls } from "../../../../../urls";
import { auditDetailLines, auditEventLabel, auditExportLabel, auditItemTypeLabel } from "./auditLogLabels";

export interface AuditLogRow {
  id: string;
  occurredAt: string;
  time: string;
  person: string;
  bySupport: boolean;
  event: string;
  fromHistory: boolean;
  item: string;
  itemLink?: string;
  details: string[];
}

type AuditLogColumnKey = "time" | "person" | "event" | "item" | "details";

export const auditLogColumns = (t: TFunction): (ExportColumn & { key: AuditLogColumnKey })[] => [
  { key: "time", label: String(t("Time")) },
  { key: "person", label: String(t("Done by")) },
  { key: "event", label: String(t("Event_audit_log")) },
  { key: "item", label: String(t("Item")) },
  { key: "details", label: String(t("Details")) },
];

export const auditLogRow = (
  t: TFunction,
  entry: AuditLogEntryDto,
  personLabels: Map<string, string> = new Map(),
): AuditLogRow => ({
  id: entry.id,
  occurredAt: entry.occurredAt,
  time: osloTimestamp(entry.occurredAt),
  person: actorLabel(t, entry.actor, personLabels),
  bySupport: entry.bySupport,
  event: auditEventLabel(t, entry.eventType, entry.item?.type),
  fromHistory: entry.fromHistory,
  item: itemLabel(t, entry, personLabels),
  itemLink: itemLink(entry.item),
  details: auditDetailLines(t, entry.details),
});

const itemLink = (item: AuditLogItemDto | null | undefined): string | undefined =>
  item?.type === "MarketDialogue" ? urls.app.marketDialogues.view.go(item.id) : undefined;

export const auditLogPersonLabels = (
  options: Pick<AuditLogFiltersDto, "people" | "targets"> | undefined,
): Map<string, string> => {
  const people = uniqueById([...(options?.people ?? []), ...(options?.targets ?? [])]);
  const sharing = new Map<string, number>();
  people.forEach((person) => {
    const key = nameKey(person.name);
    sharing.set(key, (sharing.get(key) ?? 0) + 1);
  });
  return new Map(
    people.map((person) => {
      const name = shownName(person.name);
      return [person.id, (sharing.get(nameKey(person.name)) ?? 0) > 1 ? `${name} (${person.email})` : name];
    }),
  );
};

const uniqueById = (people: AuditLogPersonDto[]): AuditLogPersonDto[] => [
  ...new Map(people.map((person) => [person.id, person])).values(),
];

const shownName = (name: string): string => name.trim().replace(/\s+/g, " ");

const nameKey = (name: string): string => shownName(name).toLocaleLowerCase("nb-NO");

export const auditLogDownloadErrorMessage = (t: TFunction, error: unknown, format: ListFormat): string => {
  if (!isTooLargeToDownload(error)) return String(t("The audit log could not be downloaded. Try again."));
  return format === "PDF"
    ? String(
        t(
          "There are more than 2 000 entries in this period, too many for a PDF. Choose Excel or CSV, or narrow the period or the filters.",
        ),
      )
    : String(
        t("There are more than 20 000 entries in this period. Narrow the period or the filters and download again."),
      );
};

const actorLabel = (t: TFunction, actor: AuditLogActorDto, personLabels: Map<string, string>): string => {
  switch (actor.kind) {
    case AuditLogActorKind.Person: {
      const labelled = actor.personId ? personLabels.get(actor.personId) : undefined;
      if (labelled) return labelled;
      return actor.name ? shownName(actor.name) : String(t("Person no longer registered"));
    }
    case AuditLogActorKind.SourceMagnetSupport:
      return String(t("SourceMagnet Support"));
    case AuditLogActorKind.ApiKey:
      return String(t("API key"));
    case AuditLogActorKind.EntraConnection:
      return actor.name
        ? String(t("Entra ID connection ({{tenant}})", { tenant: actor.name }))
        : String(t("Entra ID connection"));
    case AuditLogActorKind.System:
      return String(t("SourceMagnet (automatic)"));
  }
};

const itemLabel = (t: TFunction, entry: AuditLogEntryDto, personLabels: Map<string, string>): string => {
  const item = entry.item;
  if (!item) return "";
  const kind = item.type === "Export" ? auditExportLabel(t, item.id) : auditItemTypeLabel(t, item.type);
  const name = itemName(item, personLabels);
  return name ? `${kind}: ${name}` : kind;
};

const itemName = (item: AuditLogItemDto, personLabels: Map<string, string>): string | undefined => {
  if (item.type === "Person") {
    const labelled = personLabels.get(item.id);
    if (labelled) return labelled;
  }
  return item.name ? shownName(item.name) : undefined;
};

const osloTimestamp = (value: string): string => {
  const parts = new Intl.DateTimeFormat("nb-NO", {
    timeZone: "Europe/Oslo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("day")}.${part("month")}.${part("year")} ${part("hour")}:${part("minute")}:${part("second")}`;
};
